export interface UserProfile {
    fullName: string;
    abhaNumber: string;
    abhaAddress: string;
    abhaAddresses?: string[];  // Multiple ABHA addresses — latest first
    mobile: string;
    status: 'ACTIVE' | 'INACTIVE' | string;
    kycStatus: 'VERIFIED' | 'PENDING' | string;
    mobileVerified: boolean;
    address: string;
    profilePhoto?: string; // Base64 encoded image
    dob?: string;
    gender?: string;
    email?: string;
    town?: string;
    district?: string;
    state?: string;
    pincode?: string;
    stateCode?: string;
    districtCode?: string;
    townCode?: string;
    memberId?: string; // Unique patient ID e.g. HPP-847291
}

export type UserRole =
    | 'ADMIN'
    | 'DOCTOR'
    | 'NURSE'
    | 'RECEPTIONIST';

export interface AuthData {
    token: string;
    tenantId: string;
    organizationName: string;
    organizationType: string;
    role: UserRole;
    branchId?: string;
    staffName?: string;
    facilityPhone?: string;
    facilityEmail?: string;
    facilityAddress?: string;
    callbackUrl?: string;
}

export type AuthConfig = {
    type: 'enrollment' | 'abha-number' | 'abha-address' | 'communication-mobile' | 'communication-mobile-user' | 'aadhaar-login' | 'find-by-mobile' | 'find-abha' | 'forgot-abha' | 'face-auth-enroll' | 'face-auth-login' | 'biometric-enroll';
    method: 'aadhaar' | 'mobile';
    value: string;
    aadhaarMobileInput: string;
    altMobile?: string; // for alternate mobile enrollment
    useLinkedMobile?: boolean; // reuse aadhaar-linked mobile
    nonKyc?: boolean; // non-KYC verification flag
    enrollmentMode?: 'aadhaar' | 'same-mobile' | 'alt-mobile' | 'dl'; // sub-mode for ABHA creation
    maskedMobile?: string; // masked mobile from ABDM OTP response e.g. "******2159"
};

export interface AccountSelection {
    preferredAbhaAddress: string;
    name: string;
    ABHANumber?: string;
    profilePhoto?: string; // Base64 encoded image
    dob?: string; // e.g., "1990-01-01"
}

export type Step = 'login' | 'authType' | 'otpRequest' | 'otpVerify' | 'userSelect' | 'abhaCreate' | 'dlEnroll' | 'aadhaarEnroll' | 'success' | 'profileManagement' | 'forgotAbha' | 'findAbha' | 'findAbhaByMobile' | 'passwordLogin' | 'faceAuthEnroll' | 'faceAuthLogin' | 'biometricEnroll';

export const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

// Flows for AuthTypeSelector
export interface FlowOption {
    id: string;
    type: string;
    method: string;
    icon: string;
    title: string;
    desc: string;
    preset?: Partial<AuthConfig>;
}

export const creationFlows: FlowOption[] = [
    { id: 'verify-aadhaar-enroll', type: 'enrollment', method: 'aadhaar', icon: '🪪', title: 'Create ABHA by Aadhaar', desc: 'Register new ABHA using Aadhaar number & OTP', preset: { enrollmentMode: 'aadhaar' } },
    { id: 'face-auth-enroll', type: 'face-auth-enroll', method: 'aadhaar', icon: '🤳', title: 'Create ABHA (Face Auth)', desc: 'Register via QR scan + face verification on hospital tablet' },
    // Biometric (RD device) flow — hidden in UI only. Backend/handlers remain
    // intact so it can be re-enabled by uncommenting this entry.
    // { id: 'biometric-enroll', type: 'biometric-enroll', method: 'aadhaar', icon: '🔒', title: 'Create ABHA (Biometric)', desc: 'Register using fingerprint, face, or iris via RD device' },
];

export const verificationFlows: FlowOption[] = [
    { id: 'verify-abha-mobile', type: 'abha-number', method: 'mobile', icon: '📞', title: 'Verify ABHA No (Mobile OTP)', desc: 'Quick verification via mobile OTP' },
    { id: 'verify-abha-aadhaar', type: 'abha-number', method: 'aadhaar', icon: '🪪', title: 'Verify ABHA No (Aadhaar OTP)', desc: 'Secure verification via Aadhaar OTP' },
    { id: 'verify-by-aadhaar', type: 'aadhaar-login', method: 'aadhaar', icon: '🔐', title: 'Verify by Aadhaar', desc: 'Verify existing ABHA using Aadhaar number & OTP' },
    { id: 'verify-abha-address-mobile', type: 'abha-address', method: 'mobile', icon: '🏠', title: 'Verify ABHA Address (Mobile OTP)', desc: 'Verify your PHR address via mobile' },
    { id: 'verify-abha-address-aadhaar', type: 'abha-address', method: 'aadhaar', icon: '🪪', title: 'Verify ABHA Address (Aadhaar OTP)', desc: 'Secure address verification via Aadhaar' },
    { id: 'non-kyc', type: 'abha-address', method: 'mobile', icon: '🔓', title: 'Non-KYC ABHA Verification', desc: 'Proceed without Aadhaar documentation' },
    { id: 'find-by-mobile', type: 'find-by-mobile', method: 'mobile', icon: '🔍', title: 'Find ABHA by Mobile', desc: 'Search & verify all ABHA IDs linked to a mobile number' },
    { id: 'face-auth-login', type: 'face-auth-login', method: 'mobile', icon: '🤳', title: 'Find ABHA (Face Auth)', desc: 'Search by mobile & verify via QR face authentication' },
];

export const utilityFlows: FlowOption[] = [
    { id: 'find-abha', type: 'find-abha', method: 'mobile', icon: '🔍', title: 'Find ABHA', desc: 'Search ABHA by number or address' },
    { id: 'forgot-abha-mobile', type: 'forgot-abha', method: 'mobile', icon: '📞', title: 'Forgot ABHA (Mobile)', desc: 'Retrieve ABHA via Mobile OTP' },
    { id: 'forgot-abha-aadhaar', type: 'forgot-abha', method: 'aadhaar', icon: '🪪', title: 'Forgot ABHA (Aadhaar)', desc: 'Retrieve ABHA via Aadhaar OTP' },
    { id: 'retrieve-enrollment', type: 'forgot-abha', method: 'mobile', icon: '📝', title: 'Retrieve Enrollment', desc: 'Retrieve enrollment number via Mobile' },
];
